<template>
  <div class="cal-view">
    <!-- Zona 1: Controles -->
    <div class="cal-view__controls">
      <div class="cal-view__range-pills">
        <button
          v-for="opt in RANGE_OPTIONS"
          :key="opt"
          class="cal-view__range-pill"
          :class="{ 'cal-view__range-pill--active': rangeMonths === opt }"
          @click="rangeMonths = opt"
        >
          {{ opt }} meses
        </button>
      </div>

      <div class="cal-view__nav">
        <button class="cal-view__nav-btn" @click="shiftMonths(-1)">
          <span class="mdi mdi-chevron-left"></span>
        </button>
        <span class="cal-view__nav-label">{{ dateRangeLabel }}</span>
        <button class="cal-view__nav-today" @click="goToday">Hoy</button>
        <button class="cal-view__nav-btn" @click="shiftMonths(1)">
          <span class="mdi mdi-chevron-right"></span>
        </button>
      </div>
    </div>

    <!-- Zona 2: Timeline -->
    <div class="cal-view__timeline">
        <div class="cal-view__timeline-scroll">
      <div class="cal-view__names-col">
        <div class="cal-view__names-header"></div>
        <div
          v-for="row in rows"
          :key="row.id"
          class="cal-view__name-row"
          :class="{ 'cal-view__name-row--selected': selectedId === row.id }"
          @click="selectRow(row)"
        >
          <span class="cal-view__name-tenant">{{ row.tenant.name }}</span>
          <span class="cal-view__name-plot">{{ row.plot.name }}</span>
        </div>
      </div>

      <div class="cal-view__grid-scroll" ref="scrollEl">
        <div class="cal-view__grid" :style="{ width: totalWidth + 'px' }">
          <div class="cal-view__month-header">
            <div
              v-for="(m, i) in visibleMonths"
              :key="i"
              class="cal-view__month-col"
              :class="{ 'cal-view__month-col--current': m.isCurrent }"
              :style="{ width: MONTH_WIDTH + 'px' }"
            >
              <span class="cal-view__month-label">{{ m.label }}</span>
              <span v-if="m.showYear" class="cal-view__month-year">{{ m.year }}</span>
            </div>
          </div>

          <div v-if="todayOffsetPx !== null" class="cal-view__today-line" :style="{ left: todayOffsetPx + 'px' }"></div>

          <div
            v-for="(row, index) in rows"
            :key="row.id"
            class="cal-view__grid-row"
            :class="{ 'cal-view__grid-row--even': index % 2 === 1, 'cal-view__grid-row--selected': selectedId === row.id }"
            @click="selectRow(row)"
          >
            <template v-if="barFor(row)">
              <div
                class="cal-view__bar"
                :class="[`cal-view__bar--${row.statusColor}`, { 'cal-view__bar--urgent': barFor(row).urgentEdge }]"
                :style="{ left: barFor(row).left + 'px', width: barFor(row).width + 'px' }"
                @mouseenter="onBarHover(row, $event)"
                @mouseleave="hoveredId = null"
              >
                <span v-if="barFor(row).width > 60" class="cal-view__bar-label">{{ row.tenant.name }}</span>
              </div>

              <div
                v-if="barFor(row).renewalExtension"
                class="cal-view__bar-extension"
                :style="{ left: barFor(row).renewalExtension.left + 'px', width: barFor(row).renewalExtension.width + 'px' }"
              ></div>

              <span
                v-for="(t, ti) in barFor(row).triangles"
                :key="ti"
                class="cal-view__escalation-triangle"
                :style="{ left: t.left + 'px' }"
                :title="`Escalación el ${formatDate(t.date)} · Nueva renta: ${formatCurrency(t.newRent)}`"
              ></span>
                    <Teleport to="body">
                    <div
                        v-if="hoveredId === row.id"
                        class="cal-view__tooltip"
                        :style="{ top: tooltipPosition.top + 'px', left: tooltipPosition.left + 'px' }"
                    >
                        <div class="cal-view__tooltip-tenant">{{ row.tenant.name }}</div>
                        <div class="cal-view__tooltip-plot">{{ row.plot.name }}<span v-if="row.plot.areaM2"> · {{ row.plot.areaM2 }} m²</span></div>
                        <div class="cal-view__tooltip-rent">{{ formatCurrency(row.rent.amount) }}</div>
                        <div class="cal-view__tooltip-dates">{{ formatDate(row.vigencia.startDate) }} — {{ formatDate(row.vigencia.endDate) }}</div>
                        <div class="cal-view__tooltip-payment">Pago: {{ paymentLabel(row.paymentStatus) }}</div>
                    </div>
                    </Teleport>
            </template>
          </div>
        </div>
      </div>
        </div>
    </div>

    <!-- Zona 3: Leyenda -->
    <div class="cal-view__legend">
      <div class="cal-view__legend-items">
        <span class="cal-view__legend-item"><span class="cal-view__legend-swatch cal-view__legend-swatch--green"></span>Al corriente</span>
        <span class="cal-view__legend-item"><span class="cal-view__legend-swatch cal-view__legend-swatch--amber"></span>Por vencer / advertencia</span>
        <span class="cal-view__legend-item"><span class="cal-view__legend-swatch cal-view__legend-swatch--red"></span>Crítico o vencido</span>
        <span class="cal-view__legend-item"><span class="cal-view__legend-swatch cal-view__legend-swatch--blue"></span>En negociación</span>
        <span class="cal-view__legend-item"><span class="cal-view__legend-swatch cal-view__legend-swatch--gray"></span>Condición especial</span>
        <span class="cal-view__legend-sep">|</span>
        <span class="cal-view__legend-item"><span class="cal-view__legend-triangle"></span>Escalación programada</span>
      </div>
      <div class="cal-view__legend-metrics">
        <span>{{ visibleRows.length }} contratos visibles</span>
        <span>{{ formatCurrency(avgRent) }} renta promedio</span>
        <span>{{ vencimientosEnRango.length }} vencimientos · {{ formatCurrency(impactoVencimientos) }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
  contracts: { type: Array, default: () => [] },
})

const tooltipPosition = ref({ top: 0, left: 0 })

function onBarHover(row, event) {
  hoveredId.value = row.id
  const rect = event.currentTarget.getBoundingClientRect()
  tooltipPosition.value = { top: rect.top - 8, left: rect.left }
}

const emit = defineEmits(['select-contract'])

const MONTH_WIDTH = 90
const RANGE_OPTIONS = [6, 12, 24]
const MONTHS_SHORT = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC']
const MONTHS_FULL = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
]

const rangeMonths = ref(12)
const selectedId = ref(null)
const hoveredId = ref(null)

const now = new Date()
const startMonth = ref({ year: now.getFullYear(), month: now.getMonth() + 1 })

function shiftMonths(delta) {
  let { year, month } = startMonth.value
  month += delta
  while (month > 12) { month -= 12; year += 1 }
  while (month < 1) { month += 12; year -= 1 }
  startMonth.value = { year, month }
}

function goToday() {
  startMonth.value = { year: now.getFullYear(), month: now.getMonth() + 1 }
}

const rows = computed(() => props.contracts)

const visibleMonths = computed(() => {
  const months = []
  let { year, month } = startMonth.value
  for (let i = 0; i < rangeMonths.value; i++) {
    months.push({
      year,
      month,
      label: MONTHS_SHORT[month - 1],
      showYear: i === 0 || month === 1,
      isCurrent: year === now.getFullYear() && month === now.getMonth() + 1,
    })
    month += 1
    if (month > 12) { month = 1; year += 1 }
  }
  return months
})

const dateRangeLabel = computed(() => {
  const start = visibleMonths.value[0]
  const end = visibleMonths.value[visibleMonths.value.length - 1]
  return `${MONTHS_FULL[start.month - 1]} ${start.year} — ${MONTHS_FULL[end.month - 1]} ${end.year}`
})

const totalWidth = computed(() => rangeMonths.value * MONTH_WIDTH)

function daysInMonth(year, month) {
  return new Date(year, month, 0).getDate()
}

// Offset continuo en "meses" desde el inicio del rango visible (con fracción de día)
function offsetMonths(date) {
  const { year: sy, month: sm } = startMonth.value
  const y = date.getFullYear()
  const m = date.getMonth() + 1
  const monthDiff = (y - sy) * 12 + (m - sm)
  const dayFraction = (date.getDate() - 1) / daysInMonth(y, m)
  return monthDiff + dayFraction
}

const todayOffsetPx = computed(() => {
  const offset = offsetMonths(now)
  if (offset < 0 || offset > rangeMonths.value) return null
  return offset * MONTH_WIDTH
})

function barFor(row) {
  const startOffset = offsetMonths(new Date(row.vigencia.startDate))
  const endOffset = offsetMonths(new Date(row.vigencia.endDate))

  const clippedStart = Math.max(0, startOffset)
  const clippedEnd = Math.min(rangeMonths.value, endOffset)
  if (clippedEnd <= 0 || clippedStart >= rangeMonths.value) return null

  const left = clippedStart * MONTH_WIDTH
  const width = Math.max(6, (clippedEnd - clippedStart) * MONTH_WIDTH)

  let renewalExtension = null
  if (row.activeProcess === 'RENEWAL') {
    const extEndOffset = Math.min(rangeMonths.value, endOffset + 1)
    if (extEndOffset > clippedEnd) {
      renewalExtension = { left: clippedEnd * MONTH_WIDTH, width: (extEndOffset - clippedEnd) * MONTH_WIDTH }
    }
  }

  const triangles = (row.escalations || [])
    .map((e) => ({ ...e, offset: offsetMonths(new Date(e.date)) }))
    .filter((e) => e.offset >= 0 && e.offset <= rangeMonths.value)
    .map((e) => ({ ...e, left: e.offset * MONTH_WIDTH }))

  return {
    left,
    width,
    renewalExtension,
    triangles,
    urgentEdge: row.vigencia.daysRemaining <= 30,
  }
}

function selectRow(row) {
  selectedId.value = row.id
  emit('select-contract', row)
}

const visibleRows = computed(() => rows.value.filter((r) => barFor(r) !== null))
const avgRent = computed(() =>
  visibleRows.value.length === 0 ? 0 : visibleRows.value.reduce((sum, r) => sum + r.rent.amount, 0) / visibleRows.value.length
)
const vencimientosEnRango = computed(() =>
  rows.value.filter((r) => {
    const offset = offsetMonths(new Date(r.vigencia.endDate))
    return offset >= 0 && offset <= rangeMonths.value
  })
)
const impactoVencimientos = computed(() => vencimientosEnRango.value.reduce((sum, r) => sum + r.rent.amount, 0))

function formatCurrency(n) {
  if (n === null || n === undefined) return '—'
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(n)
}

function formatDate(iso) {
  return new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })
}

function paymentLabel(ps) {
  if (ps.label === 'al-corriente') return 'Al corriente'
  if (ps.label === 'parcial') return `Parcial (${ps.percentPaid}%)`
  if (ps.label === 'vencido') return `Vencido (${ps.daysOverdue}d)`
  return 'Sin registro'
}
</script>

<style scoped>
.cal-view {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  background: var(--bg-surface-1);
  border: 1px solid var(--border-active);
  border-radius: 10px;
  overflow: hidden;
  margin: 0 1.5rem 1.5rem;
  container-type: inline-size;
  container-name: cal-view;
}

@container cal-view (max-width: 900px) {
  .cal-view .cal-view__nav-label {
    min-width: 160px;
    font-size: 12px;
  }
  .cal-view .cal-view__legend-item:nth-last-child(-n+2) {
    display: none;
  }
}

@container cal-view (max-width: 700px) {
  .cal-view .cal-view__legend-metrics span:nth-child(2),
  .cal-view .cal-view__legend-metrics span:nth-child(3) {
    display: none;
  }
  .cal-view .cal-view__legend-item:nth-child(n+4) {
    display: none;
  }
}

/* Zona 1 */
.cal-view__controls {
  height: 40px;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 0 1rem;
  border-bottom: 1px solid #f1f5f9;
  flex-shrink: 0;
}

.cal-view__range-pills {
  display: flex;
  gap: 0.25rem;
}

.cal-view__range-pill {
  background: none;
  border: 1px solid var(--border-active);
  border-radius: 6px;
  padding: 0.1rem 0.5rem;
  font-family: var(--font-body);
  font-size: 11px;
  color: var(--text-content);
  cursor: pointer;
  line-height: 1.4;
}

.cal-view__range-pill--active {
  background: #1e3a5f;
  border-color: #1e3a5f;
  color: #ffffff;
}

.cal-view__nav {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  justify-content: center;
}

.cal-view__nav-btn {
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  display: flex;
}

.cal-view__nav-label {
  font-family: var(--font-body);
  font-size: 13px;
  font-weight: 500;
  color: #1e3a5f;
  min-width: 220px;
  text-align: center;
}

.cal-view__nav-today {
  background: none;
  border: 1px solid var(--border-active);
  border-radius: 6px;
  padding: 0.15rem 0.5rem;
  font-family: var(--font-body);
  font-size: 11px;
  color: var(--text-content);
  cursor: pointer;
}

.cal-view__group-by-label {
  font-family: var(--font-body);
  font-size: 11px;
  color: #94a3b8;
}

.cal-view__group-by-select {
  font-family: var(--font-body);
  font-size: 12px;
  border: 1px solid var(--border-active);
  border-radius: 6px;
  padding: 0.15rem 0.4rem;
  color: var(--text-content);
}

/* Zona 2 */
.cal-view__timeline {
  flex: 1;
  min-height: 0;
  display: flex;
  overflow: hidden;
}

.cal-view__timeline-scroll {
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: flex-start;
  overflow-y: auto;
}

.cal-view__names-col {
  width: 180px;
  flex-shrink: 0;
  border-right: 1px solid #f1f5f9;
}

.cal-view__names-header {
  height: 32px;
  background: #f8f9fc;
  border-bottom: 1px solid #e2e8f0;
}
.cal-view__grid-scroll {
  flex: 1;
  overflow-x: auto;
  overflow-y: hidden;
}

.cal-view__timeline-scroll::-webkit-scrollbar {
  width: 6px;
}

.cal-view__timeline-scroll::-webkit-scrollbar-thumb {
  background: var(--border-active);
  border-radius: 3px;
}

.cal-view__timeline-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.cal-view__grid-scroll::-webkit-scrollbar {
  height: 6px;
}

.cal-view__grid-scroll::-webkit-scrollbar-thumb {
  background: var(--border-active);
  border-radius: 3px;
}

.cal-view__grid-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.cal-view__name-row {
  height: 40px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0 0.75rem;
  cursor: pointer;
  border-bottom: 1px solid #f1f5f9;
}

.cal-view__name-row--selected {
  background: #eff6ff;
}

.cal-view__name-tenant {
  font-family: var(--font-body);
  font-size: 13px;
  font-weight: 500;
  color: #1e3a5f;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cal-view__name-plot {
  font-size: 10px;
  color: #64748b;
  background: #f1f5f9;
  border-radius: 4px;
  padding: 0.05rem 0.3rem;
  width: fit-content;
  margin-top: 0.15rem;
}

.cal-view__grid {
  position: relative;
}

.cal-view__month-header {
  display: flex;
  height: 32px;
  background: #f8f9fc;
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 0;
  z-index: 5;
}

.cal-view__month-col {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-right: 1px solid #f1f5f9;
}

.cal-view__month-col--current {
  background: #eff6ff;
}

.cal-view__month-label {
  font-family: var(--font-body);
  font-size: 10px;
  font-weight: 500;
  color: #94a3b8;
  text-transform: uppercase;
}

.cal-view__month-year {
  font-size: 9px;
  color: #cbd5e1;
}

.cal-view__today-line {
  position: absolute;
  top: 32px;
  bottom: 0;
  width: 1px;
  background: #3b82f6;
  z-index: 4;
}

.cal-view__grid-row {
  height: 40px;
  position: relative;
  border-bottom: 1px solid #f1f5f9;
  background: #ffffff;
}

.cal-view__grid-row--even {
  background: #fafafa;
}

.cal-view__grid-row:hover,
.cal-view__grid-row--selected {
  background: #eff6ff;
}

.cal-view__bar {
  position: absolute;
  top: 8px;
  height: 24px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  padding: 0 0.5rem;
  cursor: pointer;
  overflow: hidden;
}

.cal-view__bar--green { background: rgba(16, 185, 129, 0.8); }
.cal-view__bar--amber { background: rgba(240, 165, 0, 0.8); }
.cal-view__bar--red { background: rgba(232, 69, 69, 0.8); }
.cal-view__bar--blue { background: rgba(59, 130, 246, 0.8); }
.cal-view__bar--gray { background: rgba(148, 163, 184, 0.8); }

.cal-view__bar--urgent {
  box-shadow: inset -2px 0 0 var(--accent-red), 2px 0 0 var(--accent-red);
}

.cal-view__bar-label {
  font-family: var(--font-body);
  font-size: 11px;
  font-weight: 500;
  color: #ffffff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cal-view__bar-extension {
  position: absolute;
  top: 8px;
  height: 24px;
  border-radius: 12px;
  background: rgba(59, 130, 246, 0.4);
  border: 1px dashed #3b82f6;
}

.cal-view__escalation-triangle {
  position: absolute;
  top: -2px;
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 6px solid #10b981;
  cursor: help;
}

.cal-view__tooltip {
  position: fixed;
  transform: translateY(-100%);
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.15);
  padding: 0.6rem 0.75rem;
  min-width: 200px;
  z-index: 1000;
  font-family: var(--font-body);
}

.cal-view__tooltip-tenant {
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 13px;
  color: #1e3a5f;
}

.cal-view__tooltip-plot {
  font-size: 11px;
  color: #64748b;
  margin-top: 0.15rem;
}

.cal-view__tooltip-rent {
  font-family: var(--font-mono);
  font-size: 14px;
  color: #1e3a5f;
  margin-top: 0.3rem;
}

.cal-view__tooltip-dates,
.cal-view__tooltip-payment {
  font-size: 11px;
  color: #64748b;
  margin-top: 0.2rem;
}

/* Zona 3 */
.cal-view__legend {
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1rem;
  border-top: 1px solid #f1f5f9;
  flex-shrink: 0;
}

.cal-view__legend-items {
  display: flex;
  align-items: center;
  gap: 0.9rem;
}

.cal-view__legend-item {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-family: var(--font-body);
  font-size: 10px;
  color: #64748b;
}

.cal-view__legend-swatch {
  width: 12px;
  height: 8px;
  border-radius: 4px;
}

.cal-view__legend-swatch--green { background: #10b981; }
.cal-view__legend-swatch--amber { background: #f0a500; }
.cal-view__legend-swatch--red { background: #e84545; }
.cal-view__legend-swatch--blue { background: #3b82f6; }
.cal-view__legend-swatch--gray { background: #94a3b8; }

.cal-view__legend-sep {
  color: #e2e8f0;
}

.cal-view__legend-triangle {
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 6px solid #10b981;
}

.cal-view__legend-metrics {
  display: flex;
  align-items: center;
  gap: 1rem;
  font-family: var(--font-mono);
  font-size: 11px;
  color: #1e3a5f;
}
</style>